'use client';

import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
  LearningModule,
  UserModuleProgress,
  ModuleStepType,
  InteractiveType,
  HybridContent
} from '../types';
import { MarkdownRenderer } from './components/MarkdownRenderer';
import { getInteractiveComponent } from './components/InteractiveComponents';
import { QuizQuestionRenderer, QuizQuestion } from './components/QuizQuestions';
import { calculateQuizResults, hasAnsweredQuestion } from './utils/quizValidation';
import { QuizCompletionScreen } from './components/QuizCompletionScreen';
import { MicroLearningAPI } from '../../../apis/micro-learning.api';
import { motion, AnimatePresence } from 'framer-motion';
import { Frown, Sparkles, Heart, Lightbulb } from 'lucide-react';
import SeedsIcon from '../../../assets/seed-icon.svg';
import { ContentRenderer, isComponentBasedContent } from '@twygs/shared/micro-learning';

// Helper to detect device type
const getDeviceType = (): string => {
  const width = window.innerWidth;
  if (width < 768) return 'mobile';
  if (width < 1024) return 'tablet';
  return 'desktop';
};

// Exit confirmation dialog component
const ExitConfirmationDialog: React.FC<{
  onStay: () => void;
  onLeave: () => void;
  seedReward?: number;
  currentStep: number;
  totalSteps: number;
}> = ({ onStay, onLeave, seedReward, currentStep, totalSteps }) => {
  const progressPercent = Math.round(((currentStep + 1) / totalSteps) * 100);
  const isAlmostDone = progressPercent >= 60;

  const messages = [
    'But we were just getting to the good part!',
    "Knowledge is power... don't leave it behind!",
    'Your brain was about to grow a little bigger!',
    'So close to becoming a bit smarter!',
    'The lightbulb was about to turn on!'
  ];

  const almostDoneMessages = [
    "You're SO close! Don't give up now!",
    "You've come this far - finish strong!",
    'The finish line is right there!',
    "Just a little more - you've got this!"
  ];

  const messageList = isAlmostDone ? almostDoneMessages : messages;
  const [randomMessage] = useState(
    () => messageList[Math.floor(Math.random() * messageList.length)]
  );

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={(e) => e.stopPropagation()}
    >
      <motion.div
        initial={{ scale: 0.8, opacity: 0, y: 20 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.8, opacity: 0, y: 20 }}
        transition={{ type: 'spring', stiffness: 300, damping: 25 }}
        className="bg-white rounded-2xl shadow-2xl max-w-sm w-full overflow-hidden"
      >
        {/* Header with sad gradient */}
        <div className="bg-gradient-to-r from-amber-400 to-orange-400 p-6 text-center relative overflow-hidden">
          <div className="text-6xl mb-2">🥺</div>
          <h3 className="text-xl font-bold text-white drop-shadow-sm">Wait, leaving already?</h3>
        </div>

        <div className="p-6">
          {/* Playful message */}
          <p className="text-gray-700 text-center mb-4 font-medium">{randomMessage}</p>

          {/* Progress indicator */}
          <div className="bg-gray-100 rounded-xl p-4 mb-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-600">Your progress</span>
              <span className="text-sm font-bold text-emerald-600">{progressPercent}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2.5 overflow-hidden">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${progressPercent}%` }}
                transition={{ duration: 0.5 }}
                className="h-full bg-gradient-to-r from-emerald-400 to-green-500 rounded-full"
              />
            </div>
            <p className="text-xs text-gray-500 mt-2 text-center">
              Step {currentStep + 1} of {totalSteps}
            </p>
          </div>

          {/* Seed reward reminder */}
          {seedReward && seedReward > 0 && (
            <div className="flex items-center justify-center gap-2 bg-amber-50 border border-amber-200 rounded-xl p-3 mb-4">
              <img src={SeedsIcon} alt="Seed" className="w-5 h-5" />
              <span className="text-sm font-medium text-amber-800">
                You'll miss out on{' '}
                <strong>
                  {seedReward} {seedReward === 1 ? 'seed' : 'seeds'}
                </strong>
                !
              </span>
              <img src={SeedsIcon} alt="Seed" className="w-5 h-5" />
            </div>
          )}

          {/* Action buttons */}
          <div className="space-y-3">
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={onStay}
              className="w-full py-3.5 bg-gradient-to-r from-emerald-500 to-green-500 text-white rounded-xl font-bold text-base shadow-lg shadow-emerald-200 flex items-center justify-center gap-2"
            >
              <Sparkles className="w-5 h-5" />
              Keep Learning!
            </motion.button>

            <button
              onClick={onLeave}
              className="w-full py-3 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-xl font-medium text-sm transition-colors flex items-center justify-center gap-1"
            >
              <Frown className="w-4 h-4" />
              Leave anyway...
            </button>
          </div>

          {/* Fun footer */}
          <p className="text-xs text-gray-400 text-center mt-4 flex items-center justify-center gap-1">
            <Heart className="w-3 h-3 text-red-400" />
            Your future self will thank you
          </p>
        </div>
      </motion.div>
    </motion.div>
  );
};

// Type guard to check if content is in hybrid format
const isHybridContent = (content: any): content is HybridContent => {
  return content && typeof content === 'object' && 'type' in content && 'data' in content;
};

// Seed reward animation component
const SeedRewardAnimation = ({ seedAmount }: { seedAmount: number }) => (
  <motion.div
    initial={{ scale: 0, opacity: 0 }}
    animate={{ scale: 1, opacity: 1 }}
    transition={{ type: 'spring', stiffness: 200, damping: 10 }}
    className="flex items-center justify-center gap-3 mb-4"
  >
    <motion.div
      animate={{
        rotate: [0, 10, -10, 0],
        scale: [1, 1.1, 1]
      }}
      transition={{
        duration: 0.6,
        repeat: 2,
        ease: 'easeInOut'
      }}
      className="w-10 h-10 flex items-center justify-center"
    >
      <img src={SeedsIcon} alt="Seed" className="w-8 h-8" />
    </motion.div>
    <motion.div
      initial={{ y: 20, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ delay: 0.3 }}
      className="bg-gradient-to-r from-green-500 to-emerald-600 text-white px-4 py-2 rounded-full font-bold text-lg"
    >
      +{seedAmount === 1 ? `${seedAmount} Seed` : `${seedAmount} Seeds`} Earned!
    </motion.div>
    <motion.div
      animate={{
        rotate: [0, 10, -10, 0],
        scale: [1, 1.1, 1]
      }}
      transition={{
        duration: 0.6,
        repeat: 2,
        ease: 'easeInOut'
      }}
      className="w-10 h-10 flex items-center justify-center"
    >
      <img src={SeedsIcon} alt="Seed" className="w-8 h-8" />
    </motion.div>
  </motion.div>
);

// Series progress component
const SeriesProgress = ({ module, userProgress }: { module: any; userProgress: any }) => {
  // Wait for series data to be loaded
  if (!module?.series?.modules) return null;

  const series = module.series;
  const completedInSeries = (userProgress?.completedModulesInSeries || 0) + 1; // +1 because this module is now completed
  const totalInSeries = series.totalModules || 0;
  const currentOrder = module.seriesOrder || 0;

  // Find next module from the modules array
  const nextModule = series.modules.find((m: any) => m.seriesOrder === currentOrder + 1);
  const isLastModule = currentOrder >= totalInSeries;

  return (
    <motion.div
      initial={{ y: 20, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ delay: 0.8 }}
      className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6"
    >
      <h4 className="text-md font-semibold text-blue-900 mb-3 flex items-center gap-2">
        <span className="text-blue-600">📚</span>
        {series.title}
      </h4>

      <div className="space-y-3">
        <div className="flex items-center justify-between text-sm">
          <span className="text-blue-700">Progress in Series:</span>
          <span className="font-medium text-blue-900">
            {completedInSeries} of {totalInSeries} modules
          </span>
        </div>

        <div className="w-full bg-blue-200 rounded-full h-2">
          <motion.div
            initial={{ width: 0 }}
            animate={{
              width: `${totalInSeries > 0 ? (completedInSeries / totalInSeries) * 100 : 0}%`
            }}
            transition={{ duration: 1, delay: 1 }}
            className="bg-gradient-to-r from-blue-500 to-blue-600 h-2 rounded-full"
          />
        </div>

        {!isLastModule && nextModule && (
          <motion.div
            initial={{ x: -20, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ delay: 1.2 }}
            className="mt-3 p-3 bg-white rounded-md border border-blue-200"
          >
            <div className="text-xs text-blue-600 font-medium mb-1">Next Module:</div>
            <div className="font-medium text-blue-900">{nextModule.title}</div>
          </motion.div>
        )}

        {isLastModule && (
          <motion.div
            initial={{ x: -20, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ delay: 1.2 }}
            className="mt-3 p-3 bg-green-50 rounded-md border border-green-200"
          >
            <div className="text-xs text-green-600 font-medium mb-1">🎉 Series Complete!</div>
            <div className="font-medium text-green-900">
              Congratulations on completing all modules!
            </div>
            <div className="text-xs text-green-700 mt-1">
              You've finished the entire "{series.title}" series
            </div>
          </motion.div>
        )}
      </div>
    </motion.div>
  );
};

interface MicroLearningModalProps {
  isOpen: boolean;
  onClose: () => void;
  moduleId: string;
  userProgress?: UserModuleProgress;
}

const MicroLearningModal: React.FC<MicroLearningModalProps> = ({
  isOpen,
  onClose,
  moduleId,
  userProgress: initialProgress
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [userAnswers, setUserAnswers] = useState<Record<string, any>>({});
  const [completed, setCompleted] = useState(false);
  const [showExplanation, setShowExplanation] = useState(false);
  const [currentExercise, setCurrentExercise] = useState(0);
  const [userProgress] = useState<UserModuleProgress | null>(initialProgress || null);
  const [fullModule, setFullModule] = useState<LearningModule | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [moduleStartTime, setModuleStartTime] = useState<number>(Date.now());
  const [showQuizCompletion, setShowQuizCompletion] = useState(false);
  const [quizResults, setQuizResults] = useState<{
    totalQuestions: number;
    correctAnswers: number;
    score: number;
    passed: boolean;
  } | null>(null);
  const [showExitConfirmation, setShowExitConfirmation] = useState(false);
  const [quizAttemptCount, setQuizAttemptCount] = useState(1); // Track quiz attempts for retry logic
  const contentScrollRef = React.useRef<HTMLDivElement>(null);

  // Metrics tracking state
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [stepStartTime, setStepStartTime] = useState<number>(Date.now());
  const stepTimeRef = useRef<Record<number, number>>({}); // Accumulate time per step
  const stepsCompletedRef = useRef<number>(0);

  // Handle close button click - show confirmation if not completed
  const handleCloseAttempt = () => {
    if (completed || loading) {
      // If completed or still loading, just close
      onClose();
    } else {
      // Show exit confirmation dialog
      setShowExitConfirmation(true);
    }
  };

  // Handle staying (from exit confirmation)
  const handleStay = () => {
    setShowExitConfirmation(false);
  };

  // Handle leaving (from exit confirmation)
  const handleLeave = async () => {
    setShowExitConfirmation(false);

    // Track abandonment and end session
    const timeSpentSeconds = Math.floor((Date.now() - moduleStartTime) / 1000);

    // Track abandonment
    try {
      await MicroLearningAPI.trackAbandonment({
        moduleId,
        abandonedAtStep: currentStep,
        timeSpent: timeSpentSeconds,
        reason: 'user_exit'
      });
    } catch (err) {
      console.warn('Failed to track abandonment:', err);
    }

    // End the session
    if (sessionId) {
      try {
        await MicroLearningAPI.endSession({
          sessionId,
          moduleId,
          outcome: 'ABANDONED',
          duration: timeSpentSeconds,
          endStep: currentStep,
          stepsProgressed: stepsCompletedRef.current,
          quizAttempted: quizAttemptCount > 1,
          quizScore: quizResults?.score,
          quizPassed: quizResults?.passed
        });
      } catch (err) {
        console.warn('Failed to end session:', err);
      }
    }

    onClose();
  };

  // Handle quiz retry (allows one retry on first failure)
  const handleQuizRetry = () => {
    setShowQuizCompletion(false);
    setQuizAttemptCount(2); // Mark as second attempt
    setUserAnswers({}); // Clear previous answers
    setCurrentExercise(0); // Reset to first exercise/question
    setShowExplanation(false);
    setCurrentStep(0); // Send user back to step 1 to review the material
  };

  // Initialize module when opened
  useEffect(() => {
    if (isOpen) {
      initializeModule();
    }
  }, [isOpen]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';

      return () => {
        document.body.style.overflow = originalOverflow;
      };
    }
  }, [isOpen]);

  // Scroll to top and track step time when step changes
  useEffect(() => {
    if (contentScrollRef.current) {
      contentScrollRef.current.scrollTop = 0;
    }

    // Track time spent on previous step
    const timeOnPreviousStep = Math.floor((Date.now() - stepStartTime) / 1000);
    if (timeOnPreviousStep > 0 && currentStep > 0) {
      const previousStep = currentStep - 1;
      stepTimeRef.current[previousStep] =
        (stepTimeRef.current[previousStep] || 0) + timeOnPreviousStep;

      // Send step progress to backend (non-blocking)
      MicroLearningAPI.trackStepProgress({
        moduleId,
        currentStep: previousStep,
        timeOnStep: timeOnPreviousStep,
        stepCompleted: true
      }).catch((err) => console.warn('Failed to track step progress:', err));
    }

    // Reset step timer for new step
    setStepStartTime(Date.now());
  }, [currentStep, currentExercise]);

  const initializeModule = async () => {
    try {
      setLoading(true);
      setError(null);
      setModuleStartTime(Date.now());
      setStepStartTime(Date.now());
      setQuizAttemptCount(1); // Reset attempt count for new module
      stepTimeRef.current = {}; // Reset step times
      stepsCompletedRef.current = 0;

      // Fetch the full module from API using the proper API function
      const moduleData = await MicroLearningAPI.getModuleById(moduleId);
      setFullModule(moduleData);

      // Fetch series metadata if module has a seriesId
      if (moduleData.seriesId) {
        try {
          const seriesMetadata = await MicroLearningAPI.getSeriesMetadataById(moduleData.seriesId);
          setFullModule((prev) => (prev ? { ...prev, series: seriesMetadata } : null));
        } catch (seriesErr) {
          console.warn('Failed to fetch series metadata:', seriesErr);
          // Continue without series data - not critical
        }
      }

      // Start a new learning session for metrics tracking
      try {
        const sessionResponse = await MicroLearningAPI.startSession({
          moduleId,
          deviceType: getDeviceType(),
          referrer: document.referrer || 'direct'
        });
        setSessionId(sessionResponse.sessionId);

        // If user has previous progress, resume from that step
        if (sessionResponse.resumeFromStep && sessionResponse.resumeFromStep > 0) {
          setCurrentStep(sessionResponse.resumeFromStep);
        }
      } catch (sessionErr) {
        console.warn('Failed to start session:', sessionErr);
        // Continue without session tracking - not critical
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to initialize module');
      console.error('Failed to initialize module:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleNext = async () => {
    const step = fullModule?.steps[currentStep];
    if (!step) return;

    // Get the actual content data (handle hybrid format)
    const stepContent = isHybridContent(step.content) ? step.content.data : step.content;

    // Check if we're in a quiz step with more questions
    if (
      step.stepType === ModuleStepType.QUIZ &&
      stepContent.questions &&
      currentExercise < stepContent.questions.length - 1
    ) {
      // Handle QUIZ steps - move to next question
      setCurrentExercise(currentExercise + 1);
      setShowExplanation(false);
      // Don't clear the answer - keep it for scoring
    } else if (
      step.stepType === ModuleStepType.INTERACTIVE &&
      stepContent.exercises &&
      currentExercise < stepContent.exercises.length - 1
    ) {
      // Handle INTERACTIVE steps - move to next exercise
      setCurrentExercise(currentExercise + 1);
      setShowExplanation(false);
      const nextKey = `${currentStep}-${currentExercise + 1}`;
      setUserAnswers({
        ...userAnswers,
        [nextKey]: undefined
      });
    } else if (currentStep < (fullModule.steps.length || 0) - 1) {
      // Check if current step is a quiz and all questions are answered
      if (step.stepType === ModuleStepType.QUIZ && stepContent.questions) {
        const allAnswered = (stepContent.questions as QuizQuestion[]).every((q, idx) =>
          hasAnsweredQuestion(q, userAnswers[idx])
        );

        if (!allAnswered) {
          alert('Please answer all quiz questions before continuing.');
          return;
        }

        // Show quiz completion screen before moving to next step
        const results = calculateQuizResults(stepContent.questions as QuizQuestion[], userAnswers);
        setQuizResults(results);
        setShowQuizCompletion(true);
        return;
      }

      // Track step completion
      stepsCompletedRef.current++;

      // Move to next step (non-quiz or after quiz completion screen)
      setCurrentStep(currentStep + 1);
      setShowExplanation(false);
      setCurrentExercise(0);
    } else {
      // Before completing, verify all quiz questions in the module are answered
      if (step.stepType === ModuleStepType.QUIZ && stepContent.questions) {
        const allAnswered = (stepContent.questions as QuizQuestion[]).every((q, idx) =>
          hasAnsweredQuestion(q, userAnswers[idx])
        );

        if (!allAnswered) {
          alert('Please answer all quiz questions before completing the module.');
          return;
        }

        // Show quiz completion screen before completing module
        const results = calculateQuizResults(stepContent.questions as QuizQuestion[], userAnswers);
        setQuizResults(results);
        setShowQuizCompletion(true);
        return;
      }

      // Complete the module (non-quiz or after quiz completion screen)
      await completeModule();
    }
  };

  const completeModule = async () => {
    try {
      // Calculate actual time spent (convert ms to seconds) and score
      const timeSpentMs = moduleStartTime ? Date.now() - moduleStartTime : 0;
      const timeSpentSeconds = Math.floor(timeSpentMs / 1000);
      const finalScore = quizResults?.score || 0;
      const passed = quizResults?.passed || false;

      // Save module completion to backend with actual metrics (time in seconds)
      await MicroLearningAPI.completeModule(moduleId, finalScore, timeSpentSeconds);

      // End the session with COMPLETED outcome
      if (sessionId) {
        try {
          await MicroLearningAPI.endSession({
            sessionId,
            moduleId,
            outcome: 'COMPLETED',
            duration: timeSpentSeconds,
            endStep: currentStep,
            stepsProgressed: stepsCompletedRef.current + 1, // +1 for current step
            quizAttempted: quizAttemptCount > 0,
            quizScore: finalScore,
            quizPassed: passed
          });
        } catch (err) {
          console.warn('Failed to end session:', err);
        }
      }

      // Emit event to notify parent components that module was completed
      window.dispatchEvent(
        new CustomEvent('moduleCompleted', {
          detail: {
            moduleId,
            completed: true,
            score: finalScore,
            passed: passed,
            timeSpent: timeSpentSeconds
          }
        })
      );
    } catch (error) {
      console.error('Failed to save module completion:', error);
      // Continue with completion even if API call fails
    }

    setCompleted(true);
  };

  const handleQuizCompletion = async () => {
    // Calculate time spent in seconds
    const timeSpentMs = moduleStartTime ? Date.now() - moduleStartTime : 0;
    const timeSpentSeconds = Math.floor(timeSpentMs / 1000);

    // Track quiz result
    if (quizResults) {
      try {
        await MicroLearningAPI.trackQuizResult({
          moduleId,
          score: quizResults.score,
          passed: quizResults.passed,
          correctAnswers: quizResults.correctAnswers,
          totalQuestions: quizResults.totalQuestions,
          attemptNumber: quizAttemptCount,
          timeSpent: timeSpentSeconds
        });
      } catch (err) {
        console.warn('Failed to track quiz result:', err);
      }
    }

    // If user failed the quiz
    if (!quizResults?.passed) {
      // Check if this is their first attempt - allow retry
      if (quizAttemptCount < 2) {
        // Don't close - the QuizCompletionScreen will show retry option
        return;
      }

      // Second failure - end session and close
      if (sessionId) {
        try {
          await MicroLearningAPI.endSession({
            sessionId,
            moduleId,
            outcome: 'QUIZ_FAILED',
            duration: timeSpentSeconds,
            endStep: currentStep,
            stepsProgressed: stepsCompletedRef.current,
            quizAttempted: true,
            quizScore: quizResults?.score || 0,
            quizPassed: false
          });
        } catch (err) {
          console.warn('Failed to end session:', err);
        }
      }

      window.dispatchEvent(
        new CustomEvent('quizFailed', {
          detail: {
            moduleId,
            passed: false,
            score: quizResults?.score || 0,
            timeSpent: timeSpentSeconds
          }
        })
      );
      // Close the modal - module will appear again in feed later
      onClose();
      return;
    }

    // If user passed and there's a seed reward, claim the seeds
    if (quizResults?.passed && fullModule?.seedReward) {
      try {
        const actualScore = quizResults.score;

        await MicroLearningAPI.claimSeeds(
          moduleId,
          fullModule.seedReward,
          actualScore,
          timeSpentSeconds
        );
        console.log(`Successfully claimed ${fullModule.seedReward} seeds!`);
      } catch (error) {
        console.error('Failed to claim seeds:', error);
        // Continue anyway - don't block the user experience
      }
    }

    // Determine if we should move to next step or complete module
    if (currentStep < (fullModule.steps.length || 0) - 1) {
      // Move to next step - hide quiz completion screen first
      setShowQuizCompletion(false);
      stepsCompletedRef.current++;
      setCurrentStep(currentStep + 1);
      setShowExplanation(false);
      setCurrentExercise(0);
      // Reset user answers for next step
      setUserAnswers({});
    } else {
      // Complete the module - this will show completion screen, so we can hide quiz completion
      await completeModule();
      // Hide quiz completion after module is completed (completion screen will show instead)
      setShowQuizCompletion(false);
    }
  };

  const handleAnswerSelect = (questionIndex: any, answerIndex: any) => {
    setUserAnswers({
      ...userAnswers,
      [questionIndex]: answerIndex
    });

    // Show explanation after answering for both INTERACTIVE and QUIZ steps
    const step = fullModule?.steps?.[currentStep];
    if (step?.stepType === ModuleStepType.INTERACTIVE || step?.stepType === ModuleStepType.QUIZ) {
      setShowExplanation(true);
    }
  };

  const getQuizScore = () => {
    const quizStep = fullModule?.steps.find((step) => step.stepType === ModuleStepType.QUIZ);
    if (!quizStep) return { totalQuestions: 0, correctAnswers: 0, score: 0, passed: false };

    // Get the actual content data (handle hybrid format)
    const stepContent = isHybridContent(quizStep.content)
      ? quizStep.content.data
      : quizStep.content;
    if (!stepContent.questions || !Array.isArray(stepContent.questions)) {
      return { totalQuestions: 0, correctAnswers: 0, score: 0, passed: false };
    }

    // Use the new validation system
    return calculateQuizResults(stepContent.questions as QuizQuestion[], userAnswers);
  };

  if (!isOpen) return null;

  console.log(fullModule);

  // Show loading state
  if (loading) {
    return createPortal(
      <div
        className="fixed inset-0 z-[999999] bg-black bg-opacity-50 flex items-center justify-center p-4"
        style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}
      >
        <div className="bg-white rounded-lg shadow-xl p-8 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading module...</p>
        </div>
      </div>,
      document.body
    );
  }

  // Show error state
  if (error) {
    return createPortal(
      <div
        className="fixed inset-0 z-[999999] bg-black bg-opacity-50 flex items-center justify-center p-4"
        style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}
      >
        <div className="bg-white rounded-lg shadow-xl p-8 text-center max-w-md">
          <div className="text-red-500 mb-4">
            <svg
              className="w-12 h-12 mx-auto"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 15.5c-.77.833.192 2.5 1.732 2.5z"
              />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Error Loading Module</h3>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600"
          >
            Close
          </button>
        </div>
      </div>,
      document.body
    );
  }

  // Don't render if we don't have the module data yet
  if (!fullModule || !fullModule.steps || fullModule.steps.length === 0) {
    return createPortal(
      <div
        className="fixed inset-0 z-[999999] bg-black bg-opacity-50 flex items-center justify-center p-4"
        style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}
      >
        <div className="bg-white rounded-lg shadow-xl p-8 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading module content...</p>
        </div>
      </div>,
      document.body
    );
  }

  const step = fullModule.steps[currentStep];

  // Helper function to render structured content (legacy format)
  const renderStructuredContent = (content: any, currentStepData: any, stepIndex: number) => {
    // Determine if this is a QUIZ or INTERACTIVE step
    const isQuiz = currentStepData.stepType === ModuleStepType.QUIZ;

    // Get the appropriate component
    const componentType = isQuiz ? 'QUIZ' : currentStepData.interactiveType || 'GUIDED_ANALYSIS';
    const InteractiveComponent = getInteractiveComponent(componentType);

    // Handle different content structures
    if (isQuiz && content.questions) {
      // QUIZ step with questions array - use new quiz renderer
      const question = content.questions[currentExercise] as QuizQuestion;
      const answerKey = currentExercise; // Use simple index for quiz answers

      return (
        <div className="mb-6">
          {content.introduction && <p className="text-gray-700 mb-6">{content.introduction}</p>}

          <div className="mb-4 text-sm text-gray-600">
            Question {currentExercise + 1} of {content.questions.length}
          </div>

          <QuizQuestionRenderer
            question={question}
            onAnswer={(answer) => handleAnswerSelect(answerKey, answer)}
            userAnswer={userAnswers[answerKey]}
            showFeedback={showExplanation}
            disabled={showExplanation}
          />
        </div>
      );
    } else if (!isQuiz && content.exercises) {
      // INTERACTIVE step with exercises array
      const exercise = content.exercises[currentExercise];
      const answerKey = `${stepIndex}-${currentExercise}`;

      return (
        <div className="mb-6">
          <p className="text-gray-700 mb-4">{content.introduction}</p>

          <InteractiveComponent
            exercise={{
              ...exercise,
              id: answerKey,
              // Add simulation-specific properties if needed for INTERACTIVE steps
              ...(currentStepData.stepType === 'INTERACTIVE' &&
                currentStepData.interactiveType === InteractiveType.SIMULATION && {
                  min: exercise.min || 0,
                  max: exercise.max || 100,
                  initialValue: exercise.initialValue || 50,
                  sliderLabel: exercise.sliderLabel || exercise.question,
                  dynamicFeedback: exercise.dynamicFeedback || [
                    { min: 0, max: 33, feedback: 'Low range - observe the changes' },
                    { min: 34, max: 66, feedback: 'Medium range - notice the shift' },
                    { min: 67, max: 100, feedback: 'High range - see the impact' }
                  ],
                  visualization: exercise.visualization || {
                    type: 'dual_values',
                    title: 'Current Values',
                    values: [
                      {
                        label: 'Value A',
                        calculation: 'inverse',
                        bgColor: 'bg-blue-100',
                        textColor: 'text-blue-800',
                        unit: '%'
                      },
                      {
                        label: 'Value B',
                        calculation: 'direct',
                        bgColor: 'bg-green-100',
                        textColor: 'text-green-800',
                        unit: '%'
                      }
                    ]
                  }
                })
            }}
            onAnswer={(answer) => handleAnswerSelect(answerKey, answer)}
            showFeedback={showExplanation}
            disabled={showExplanation}
          />
        </div>
      );
    } else if (!isQuiz && content.scenario) {
      // Handle scenario-based interactive content (APPLIED_PRACTICE, etc.)
      const scenario = content.scenario;
      const answerKey = `scenario-${stepIndex}`;

      return (
        <div className="mb-6">
          <p className="text-gray-700 mb-4">{content.introduction}</p>

          <InteractiveComponent
            exercise={{
              ...scenario,
              id: answerKey,
              content: scenario.event || scenario.context,
              question: scenario.question,
              options: scenario.options,
              correctAnswer: scenario.correctAnswer,
              explanation: scenario.explanation
            }}
            onAnswer={(answer) => handleAnswerSelect(answerKey, answer)}
            showFeedback={showExplanation}
            disabled={showExplanation}
          />
        </div>
      );
    }

    // Default interactive rendering - use the component system
    return (
      <div className="mb-6">
        <p className="text-gray-700 mb-4">{content.introduction || 'Interactive content'}</p>

        <InteractiveComponent
          exercise={{
            ...content,
            id: `${currentStep}-default`
          }}
          onAnswer={(answer) => handleAnswerSelect(`${currentStep}-default`, answer)}
          showFeedback={showExplanation}
          disabled={showExplanation}
        />
      </div>
    );
  };

  // Render different content based on step type and structure
  const renderStepContent = () => {
    // Check if this is component-based content (NEW)
    if (isComponentBasedContent(step.content)) {
      return (
        <div className="mb-6">
          <ContentRenderer content={step.content} />
        </div>
      );
    }

    // Check if this is the hybrid format (markdown/structured)
    if (isHybridContent(step.content)) {
      if (step.content.type === 'markdown' && typeof step.content.data === 'string') {
        return (
          <div className="mb-6">
            <MarkdownRenderer content={step.content.data} />
            {step.mediaUrl && (
              <div className="my-4">
                <img src={step.mediaUrl} alt="Illustration" className="w-full h-auto rounded-lg" />
              </div>
            )}
          </div>
        );
      } else if (step.content.type === 'structured' && typeof step.content.data === 'object') {
        const structuredContent = step.content.data;
        return renderStructuredContent(structuredContent, step, currentStep);
      }
    }

    // Legacy format handling - use existing content as structured data
    return renderStructuredContent(step.content, step, currentStep);
  };

  // Completion screen
  if (completed) {
    const score = getQuizScore();
    const hasSeedReward = fullModule?.seedReward && fullModule.seedReward > 0;

    return createPortal(
      <div
        className="fixed inset-0 z-[999999] bg-black bg-opacity-50 flex items-center justify-center p-4"
        style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}
        onWheel={(e) => e.stopPropagation()}
      >
        <div
          className="relative bg-white rounded-lg shadow-xl max-w-xl w-full max-h-[90vh] overflow-auto"
          onWheel={(e) => e.stopPropagation()}
        >
          <button
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-500"
            onClick={onClose}
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>

          <div className="p-6 text-center min-h-[400px] flex flex-col justify-center">
            {/* Seed Reward Animation */}
            {hasSeedReward && (
              <motion.div
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.2, type: 'spring', stiffness: 200, damping: 10 }}
                className="mb-6"
              >
                <SeedRewardAnimation seedAmount={fullModule.seedReward} />
              </motion.div>
            )}

            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: hasSeedReward ? 0.8 : 0.2 }}
              className="mb-6"
            >
              <motion.div
                className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-emerald-100 mb-4"
                animate={{
                  scale: [1, 1.1, 1],
                  rotate: [0, 5, -5, 0]
                }}
                transition={{
                  duration: 0.8,
                  delay: 0.5,
                  repeat: 1
                }}
              >
                <svg
                  className="w-8 h-8 text-emerald-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M5 13l4 4L19 7"
                  ></path>
                </svg>
              </motion.div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Module Completed!</h3>
            </motion.div>

            {/* Series Progress */}
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: hasSeedReward ? 1.2 : 0.8 }}
            >
              <SeriesProgress module={fullModule} userProgress={userProgress} />
            </motion.div>

            {/* Key Takeaways */}
            {(fullModule?.keyTakeaways || []).length > 0 && (
              <motion.div
                className="p-5 rounded-lg mb-6 bg-emerald-50 border border-emerald-200"
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: hasSeedReward ? 1.4 : 1 }}
              >
                <div className="flex items-center gap-2 mb-3">
                  <Lightbulb className="w-5 h-5 text-emerald-600" />
                  <h4 className="text-md font-semibold text-emerald-800">Key Takeaways</h4>
                </div>
                <ul className="text-sm space-y-2 text-emerald-700">
                  {(fullModule?.keyTakeaways || []).map((takeaway, index) => (
                    <motion.li
                      key={index}
                      className="flex items-start gap-2"
                      initial={{ x: -20, opacity: 0 }}
                      animate={{ x: 0, opacity: 1 }}
                      transition={{ delay: hasSeedReward ? 1.5 + index * 0.1 : 1.1 + index * 0.1 }}
                    >
                      <span className="inline-block w-1.5 h-1.5 rounded-full mt-2 flex-shrink-0 bg-emerald-500" />
                      <span>{takeaway}</span>
                    </motion.li>
                  ))}
                </ul>
              </motion.div>
            )}

            {/* Action Button */}
            <motion.button
              onClick={onClose}
              className="w-full px-8 py-4 rounded-lg font-semibold text-white text-base md:text-lg shadow-lg bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700"
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: hasSeedReward ? 1.7 : 1.3 }}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              Back to Feed →
            </motion.button>
          </div>
        </div>
      </div>,
      document.body
    );
  }

  // Quiz completion screen
  if (showQuizCompletion && quizResults) {
    return createPortal(
      <div
        className="fixed inset-0 z-[999999] bg-black bg-opacity-50 flex items-center justify-center p-4"
        style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}
        onWheel={(e) => e.stopPropagation()}
      >
        <div className="relative max-w-xl w-full" onWheel={(e) => e.stopPropagation()}>
          <QuizCompletionScreen
            score={quizResults.score}
            correctAnswers={quizResults.correctAnswers}
            totalQuestions={quizResults.totalQuestions}
            passed={quizResults.passed}
            onContinue={handleQuizCompletion}
            onRetry={handleQuizRetry}
            seedReward={fullModule?.seedReward}
            onClose={onClose}
            attemptCount={quizAttemptCount}
          />
        </div>
      </div>,
      document.body
    );
  }

  // Normal step rendering
  return createPortal(
    <div
      className="fixed inset-0 z-[999999] bg-black bg-opacity-50 flex items-center justify-center p-4"
      style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}
      onWheel={(e) => e.stopPropagation()}
    >
      <div
        className="relative bg-white rounded-lg shadow-xl max-w-xl w-full max-h-[80vh] flex flex-col"
        onWheel={(e) => e.stopPropagation()}
      >
        {/* Sticky Header */}
        <div className="sticky top-0 bg-white rounded-t-lg border-b border-gray-200 p-6 pb-4 pr-12 z-10">
          <button
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-500 z-20"
            onClick={handleCloseAttempt}
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>

          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600 font-medium">
                Step {currentStep + 1} of {fullModule.steps.length || 0}
              </span>
              {fullModule?.seriesOrder && (
                <span className="text-xs font-medium text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full">
                  Part {fullModule.seriesOrder}
                </span>
              )}
            </div>
            <div className="w-full max-w-32 bg-gray-200 rounded-full h-1.5 ml-4">
              <div
                className="bg-emerald-500 h-1.5 rounded-full transition-all duration-300"
                style={{ width: `${((currentStep + 1) / (fullModule.steps.length || 1)) * 100}%` }}
              ></div>
            </div>
          </div>
          <h3 className="text-xl font-bold text-gray-900">{step.title}</h3>
        </div>

        {/* Scrollable Content */}
        <div ref={contentScrollRef} className="flex-1 overflow-y-auto p-6 pt-4">
          {renderStepContent()}
        </div>

        {/* Sticky Footer */}
        <div className="sticky bottom-0 bg-white rounded-b-lg border-t border-gray-200 p-4 flex justify-between z-10">
          <button
            className="px-4 py-2 text-gray-900 bg-white border border-gray-300 rounded-md font-medium hover:bg-gray-50"
            onClick={currentStep > 0 ? () => setCurrentStep(currentStep - 1) : handleCloseAttempt}
          >
            {currentStep > 0 ? 'Previous' : 'Exit'}
          </button>

          <button
            className="px-4 py-2 bg-emerald-500 text-white rounded-md font-medium hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={handleNext}
            disabled={(() => {
              // Get the actual content data (handle hybrid format)
              const stepContent = isHybridContent(step.content) ? step.content.data : step.content;

              // For QUIZ steps, check if current question is answered using new validation
              if (step.stepType === ModuleStepType.QUIZ && stepContent.questions) {
                const currentQuestion = stepContent.questions[currentExercise] as QuizQuestion;
                const hasAnswered = hasAnsweredQuestion(
                  currentQuestion,
                  userAnswers[currentExercise]
                );
                return !showExplanation && !hasAnswered;
              }

              // For INTERACTIVE steps, use existing logic
              return (
                (step.stepType === ModuleStepType.INTERACTIVE &&
                  step.interactiveType === InteractiveType.GUIDED_ANALYSIS &&
                  !showExplanation &&
                  userAnswers[`${currentStep}-${currentExercise}`] === undefined) ||
                (step.stepType === ModuleStepType.INTERACTIVE &&
                  step.interactiveType === InteractiveType.APPLIED_PRACTICE &&
                  !showExplanation &&
                  userAnswers[`scenario-${currentStep}`] === undefined) ||
                (step.stepType === ModuleStepType.INTERACTIVE &&
                  !step.interactiveType &&
                  userAnswers[0] === undefined)
              );
            })()}
          >
            {(() => {
              const stepContent = isHybridContent(step.content) ? step.content.data : step.content;

              // Check if we're in a quiz step with more questions
              if (step.stepType === ModuleStepType.QUIZ && stepContent.questions) {
                if (currentExercise < stepContent.questions.length - 1) {
                  return 'Next Question';
                }
                // On last question of quiz
                return 'Finish Quiz';
              }

              // Check if we're in an interactive step with more exercises
              if (
                step.stepType === ModuleStepType.INTERACTIVE &&
                stepContent.exercises &&
                currentExercise < stepContent.exercises.length - 1
              ) {
                return 'Next Example';
              }

              // Check if we're moving to the next step
              if (currentStep < (fullModule.steps.length || 0) - 1) {
                return 'Next';
              }

              return 'Finish';
            })()}
          </button>
        </div>
      </div>

      {/* Exit Confirmation Dialog */}
      <AnimatePresence>
        {showExitConfirmation && fullModule && (
          <ExitConfirmationDialog
            onStay={handleStay}
            onLeave={handleLeave}
            seedReward={fullModule.seedReward}
            currentStep={currentStep}
            totalSteps={fullModule.steps?.length || 1}
          />
        )}
      </AnimatePresence>
    </div>,
    document.body
  );
};

export default MicroLearningModal;
